import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
} from '@mui/material';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Fragment, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormTreeSelectField from 'components/ui/form/FormTreeSelectField';
import FormSimpleSelectField from 'components/ui/form/FormSimpleSelectField';
import FormSelectField from 'components/ui/form/FormSelectField';
import useFormSubmit from 'hooks/useFormSubmit';
import { useQuery } from 'react-query';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

const validationSchema = yup.object({
  crm_block_item_id: yup
    .string()
    .required('settings.targets.validation.blockId'),
});

const FbAddEditModal = (props) => {
  const { open, setOpen, refetch = () => {}, data } = props;
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [step, setStep] = useState(1);
  const [hasError, setHasError] = useState(false);
  const { submit, isSubmitting } = useFormSubmit();

  const formik = useFormik({
    initialValues: {
      facebook_id: '',
      page_id: '',
      form_id: '',

      crm_block_item_id: '',
      crm_block_id: '',
    },
    validationSchema: validationSchema,

    onSubmit: async (values) => {
      submit(
        { type: 'post', contentType: 'simple' },
        values,
        '/admin/facebook/mapping',
        values.client_id,
        null,
        true,
        handleFinishRequest,
      );
    },
  });

  const { data: tokens } = useQuery({
    queryKey: ['admin/facebook/tokens'],
    queryFn: async function () {
      const response = await axiosPrivate.get('admin/facebook/tokens');
      return response.data.data || [];
    },
    onSuccess: (list) => {
      // Agar bitta token bo'lsa - auto-pick
      if (list?.length === 1 && !formik.values.facebook_id) {
        formik.setFieldValue('facebook_id', list[0].id);
      }
    },
    enabled: !hasError,
    onError: () => setHasError(true),
    retry: false,
  });

  // Tanlangan sahifaning forma ro'yxati (status badge uchun)
  const { data: pageForms, refetch: refetchPageForms } = useQuery({
    queryKey: ['admin/facebook/forms', formik.values.page_id],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `admin/facebook/forms/${formik.values.page_id}`,
      );
      return response.data.data || [];
    },
    enabled: !hasError && !!formik.values.page_id,
    onError: () => setHasError(true),
    retry: false,
  });

  const selectedFormInfo = useMemo(() => {
    if (!formik.values.form_id || !pageForms?.length) return null;
    return pageForms.find((f) => String(f.id) === String(formik.values.form_id));
  }, [formik.values.form_id, pageForms]);

  const { data: forms } = useQuery({
    queryKey: ['admin/facebook/form/', formik.values.form_id],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `admin/facebook/form/${formik.values.form_id}`,
      );
      return response.data.data;
    },
    onSuccess: (res) => {
      if (res?.length) {
        res?.forEach((f, i) => {
          // Derive target string from flags for select display
          let target = '';
          if (Number(f?.is_phone)) target = 'is_phone';
          else if (Number(f?.is_contact_name)) target = 'is_contact_name';
          else if (Number(f?.is_lead_name)) target = 'is_lead_name';
          else if (f?.field_id) target = String(f.field_id);

          formik.setFieldValue(`${i}`, {
            id: f?.id,
            field_id: f?.field_id,
            is_phone: Number(f?.is_phone) || 0,
            is_contact_name: Number(f?.is_contact_name) || 0,
            is_lead_name: Number(f?.is_lead_name) || 0,
            target,
          });
        });
      }
    },
    enabled: !hasError && !!formik.values.form_id,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const { data: fields } = useQuery({
    queryKey: ['/crm/crm-settings/fields'],
    queryFn: async function () {
      const response = await axiosPrivate.get('/crm/crm-settings/fields');
      return response.data.data;
    },
    enabled: !hasError,

    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const handleFinishRequest = () => {
    refetch();
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
    setStep(1);
  };

  const fieldOptions = useMemo(() => {
    const list = fields?.map((f) => ({ id: String(f?.id), name: f?.name }));
    list?.unshift(
      { id: 'is_phone', name: t('settings.crm.fb.mapping.phone') },
      { id: 'is_contact_name', name: t('settings.crm.fb.mapping.contactName') },
      { id: 'is_lead_name', name: t('settings.crm.fb.mapping.leadName') },
    );
    return list;
  }, [fields, t]);

  const changeFn = (id, i) => {
    const v = formik?.values?.[`${i}`] || {};
    const base = {
      ...v,
      is_phone: 0,
      is_contact_name: 0,
      is_lead_name: 0,
      field_id: null,
      target: id,
    };
    if (id === 'is_phone') {
      formik?.setFieldValue(`${i}`, { ...base, is_phone: 1 });
    } else if (id === 'is_contact_name') {
      formik?.setFieldValue(`${i}`, { ...base, is_contact_name: 1 });
    } else if (id === 'is_lead_name') {
      formik?.setFieldValue(`${i}`, { ...base, is_lead_name: 1 });
    } else {
      formik?.setFieldValue(`${i}`, { ...base, field_id: id });
    }
  };

  const handleSync = async () => {
    if (!formik.values.form_id) return;
    try {
      const res = await axiosPrivate.post(
        `/admin/facebook/forms/${formik.values.form_id}/sync-leads`,
      );
      const r = res?.data?.data || {};
      alert(
        `Sync OK\nFetched: ${r.fetched ?? 0}\nCreated: ${r.created ?? 0}\nSkipped (duplicate): ${r.skipped ?? 0}`,
      );
    } catch (e) {
      alert(
        'Sync xato: ' +
          (e?.response?.data?.message || e?.message || 'noma\'lum xato'),
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='lg'
      PaperProps={{ className: '!base-border !shadow-none' }}
    >
      <DialogTitle id='alert-dialog-title'>
        {data?.id ? (
          <span>
            {t('settings.crm.modal.editOnlinePbx')} Url:pbx12345.onpbx.ru
          </span>
        ) : (
          <span>{t('settings.crm.modal.addFb')}</span>
        )}
        <div className='close-btn-wrapper'>
          <IconButton variant='onlyIcon' color='primary' onClick={handleClose}>
            <i className='bi bi-x' />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent className='!max-w-[600px]'>
        <form onSubmit={formik.handleSubmit}>
          {step == 1 && (
            <Grid
              container
              spacing={{ xs: 2, sm: 3, lg: 3 }}
              rowSpacing={1}
              columns={{ xs: 12, sm: 12, lg: 12 }}
            >
              <Grid item={true} lg={12} sm={12} xs={12}>
                <FormSimpleSelectField
                  duration={0}
                  label={t('common.fields.account') || 'Account'}
                  fieldName='facebook_id'
                  formik={formik}
                  options={tokens || []}
                  itemValue='id'
                  itemLabel='name'
                />
              </Grid>
              <Grid item={true} lg={12} sm={12} xs={12}>
                <FormSelectField
                  duration={0}
                  label={t('common.fields.pages')}
                  fieldName='page_id'
                  path={
                    formik.values.facebook_id
                      ? `admin/facebook/getpages/${formik.values.facebook_id}`
                      : ''
                  }
                  formik={formik}
                  pathChangeable
                />
              </Grid>
              <Grid item={true} lg={12} sm={12} xs={12}>
                <FormSelectField
                  duration={0}
                  label={t('common.fields.form')}
                  fieldName='form_id'
                  path={`admin/facebook/forms/${formik.values?.page_id}`}
                  formik={formik}
                  pathChangeable
                />
                {selectedFormInfo && (
                  <div className='mt-2 flex items-center gap-2 text-xs flex-wrap'>
                    {selectedFormInfo.is_mapped ? (
                      <>
                        <span className='inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium'>
                          <i className='bi bi-check-circle-fill' />
                          {t('settings.crm.fb.savedTo')}:{' '}
                          {selectedFormInfo.pipe_name || '—'}
                        </span>
                        <Button
                          size='small'
                          variant='outlined'
                          color='success'
                          onClick={handleSync}
                          startIcon={<i className='bi bi-arrow-repeat' />}
                        >
                          {t('settings.crm.fb.syncLeads')}
                        </Button>
                      </>
                    ) : (
                      <span className='inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium'>
                        <i className='bi bi-exclamation-circle-fill' />
                        {t('settings.crm.fb.notMapped')}
                      </span>
                    )}
                  </div>
                )}
              </Grid>

              <Grid item={true} lg={12} sm={12} xs={12}>
                <Button
                  type='button'
                  variant='contained'
                  onClick={handleClose}
                  className='!mr-1 !bg-[#FF6D6D] hover:!bg-[#eb5757] !shadow-none !rounded-[10px]'
                  color='error'
                >
                  {t('common.button.cancel')}
                </Button>
                <Button
                  variant='contained'
                  color='info'
                  type='button'
                  onClick={() => setStep(2)}
                  disabled={
                    !(
                      formik.values.facebook_id &&
                      formik.values.page_id &&
                      formik.values.form_id
                    )
                  }
                >
                  {t('common.button.next')}
                </Button>
              </Grid>
            </Grid>
          )}
          {step == 2 && (
            <Grid
              container
              spacing={{ xs: 2, sm: 3, lg: 3 }}
              rowSpacing={1}
              columns={{ xs: 12, sm: 12, lg: 12 }}
            >
              <Grid item={true} lg={12} sm={12} xs={12}>
                <span className='text-sm'>
                  {t('common.fields.facebookPipe')}
                </span>
                <div className='border border-gray-400 mt-2 p-[5px] rounded-lg [&>*]:!m-0'>
                  <FormTreeSelectField
                    parentFieldName='crm_block_id'
                    childFieldName='crm_block_item_id'
                    label=''
                    childOptionField={'pipes'}
                    formik={formik}
                    parentLabel={'name'}
                    parentValue={'id'}
                    childLabel={'name'}
                    childValue={'id'}
                    variant='standard'
                    path='/crm/block'
                    size='small'
                    onChange={() => {}}
                    progress={false}
                  />
                </div>
              </Grid>
              {forms?.map((form, i) => (
                <Fragment key={i}>
                  <Grid className='border-b' item={true} lg={6} sm={6} xs={6}>
                    <div className='h-full flex items-center'>
                      {form?.facebook_field}
                    </div>
                  </Grid>
                  <Grid className='border-b' item={true} lg={6} sm={6} xs={6}>
                    <FormSimpleSelectField
                      options={fieldOptions}
                      formik={formik}
                      fieldName={`${i}.target`}
                      changeFn={(e) => changeFn(e, i)}
                    />
                  </Grid>
                </Fragment>
              ))}

              <Grid item={true} sm={12} xs={12}>
                <FormActionButtons
                  duration={0}
                  isSubmitting={isSubmitting}
                  formType='dialog'
                  setOpen={handleClose}
                  reset={formik.resetForm}
                />
              </Grid>
            </Grid>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(FbAddEditModal);
